<template>
    <div class="video-container">
      <loader v-if="isLoading" class="loader" />
      <i v-if="this.source.mimeType === 'audio/mp3' && !isLoading" @click="handleMediaPlayPause" class="fa fa-radio is-radio fa-5x"></i>
      <i 
        v-if=" this.articleAssetsData.data !== null && this.articleAssetsData.data.isEdited" 
        class="fa fa-info-circle is-edited" 
        v-tooltip.right="`${$t('mediaClipper.tooltips.user_id')} <strong>${this.articleAssetsData.data.isEdited.userId}</strong> - ${$t('mediaClipper.tooltips.last_edit')} <strong>${this.articleAssetsData.data.isEdited.updatedAt}</strong>`" 
        aria-hidden="true"
      ></i>
      <div class="controls-container d-flex items-center" :class="{ 'paused': !isMediaPlaying }">
        <div @click="handleMediaPlayPause"></div>
        <div class="d-flex items-center custom-volume-slider" v-if="!isLoading">
          <i @click="toggleMute" class="text-white fa link-pointer mr4" style="width: 2rem;"
          :class="{
            'fa-volume-up':  volume >= 0.5,
            'fa-volume-down': volume > 0 && volume < 0.5,
            'fa-volume-off': volume  <= 0
          }"
          aria-hidden="true"></i>
          <div class="volume-bar" ref="volumeContainer" @mousedown="startVolDrag">
            <div class="handle" :style="{ left: this.volHandlePos + '%' }"></div>
          </div>
        </div>
      </div>
      <video @click="handleMediaPlayPause" class="video-js video-player" controls ref="videoPlayer"></video>
    </div>
  </template>
  
  <script>
  import videojs from 'video.js';
  import 'video.js/dist/video-js.css';
  import Loader from '../loader.vue';
  import { CLIPPER_HOST, CLIPPER_HOST_NEW } from '../../config/app-const';
  import { mapActions, mapState } from 'vuex';
  
  
  
  export default {
    name: 'VideoPlayer',
    props: {
      source: {
        type: Object,
        default: () => {},
      },
      fastForwardTime:{
        type: Number,
        required: true
      },
      rewindOrFastForwardTime:{
        type: Number,
        required: true
      },
    },
    components: {
      Loader,
    },
    data() {
      return {
        player: null,
        keyframeCancellationToken: null,
        isMediaPlaying: false,
        isMuted: true,
        isLoading: true,
        isTimelineHovered: false,
        isKeyframeLoadingCanceled: false,
        totalDuration:0,
        volume: 0,
        volHandlePos: 0,
        fetchControllers: {},
        imageBlobObjects: [],
        activeDownloads: 0,
        maxConcurrentDownloads: 30,
      };
    },
    computed: {
      ...mapState({
        keyframes: state => state.mediaClipper.keyframes.data,
        articleAssetsData: state => state.mediaClipper.articleAssetsData,      
      }),
    },
    watch: {
      keyframes(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.source.keyframes = newVal;
          if (this.player.currentSrc() === this.source.originalAssetUnExtendedData.src && !this.source.originalAssetUnExtendedData.keyframes) {
            this.source.originalAssetUnExtendedData.keyframes = newVal;
          }
          else if (this.player.currentSrc() === this.source.originalAssetExtendedData.src) {
            this.source.originalAssetExtendedData.keyframes = newVal;
          }
        }
      }
    },
    methods: {
      ...mapActions({
        fetchMp4Keyframes: "mediaClipper/fetchMp4Keyframes",
      }),
      setIsHovering(state){
        this.isTimelineHovered = state
      },
      preloadKeyframesNearPlayhead(currentTime, primaryBuffer = 30) {
        this.cancelUnneededKeyframeImages(currentTime, primaryBuffer);
        if (!this.isTimelineHovered) return;
        const loadKeyframe = (index) => {
          const keyframeUrl = this.source.keyframes[index];
          if (keyframeUrl && (!this.imageBlobObjects[index] || this.imageBlobObjects[index].status === 'error') || this.imageBlobObjects.length === 0) {
            this.loadKeyframeImage(keyframeUrl, index, currentTime);
          }
        };
        loadKeyframe(currentTime);
        const loadRange = (start, end) => {
          for (let i = start; i <= end; i++) {
            if (i !== currentTime) { // Prevent reloading the current time keyframe
              loadKeyframe(i);
            }
          }
        };
        const primaryStartIndex = Math.max(0, currentTime - primaryBuffer);
        const primaryEndIndex = Math.min(this.source.keyframes.length, currentTime + primaryBuffer);
        loadRange(primaryStartIndex, primaryEndIndex);
      },
      loadKeyframeImage(keyframeUrl, index, currentTime) {
        if (this.isKeyframeLoadingCanceled) return;
        if (this.imageBlobObjects[index] && this.imageBlobObjects[index].status === 'loading') {
          return; // Prevent multiple fetches for the same keyframe
        }
        const controller = new AbortController();
        const signal = controller.signal;
        this.fetchControllers[index] = controller;
  
        this.$set(this.imageBlobObjects, index, { status: 'loading', blobUrl: null });
        if (this.imageBlobObjects[index].status !== 'loaded' && keyframeUrl) {
          this.activeDownloads++;
          fetch(CLIPPER_HOST_NEW + keyframeUrl, { signal })
            .then(response => response.blob())
            .then(blob => {
              if (this.isKeyframeLoadingCanceled) return;
              const imageUrl = URL.createObjectURL(blob);
              this.$set(this.imageBlobObjects, index, { status: 'loaded', blobUrl: imageUrl });
              this.activeDownloads--;
            })
            .catch(error => {
              this.activeDownloads--;
              if (error.name !== 'AbortError') {
                console.error('Error loading image:', keyframeUrl, error);
              }
              this.$set(this.imageBlobObjects, index, { status: 'error', blobUrl: null });
            });
        }
      },
      displayLoadedKeyframe(currentTime, retryInterval = 2000, maxRetries = 3) {
        if (this.keyframeCancellationToken) {
          clearTimeout(this.keyframeCancellationToken);
          this.keyframeCancellationToken = null;
        }
        if (this.imageBlobObjects[currentTime]?.status === 'loaded') {
          this.$emit('setCurrentKeyframe', this.imageBlobObjects[currentTime].blobUrl);
        } else if (maxRetries > 0) {
          this.keyframeCancellationToken = setTimeout(() => {
            this.displayLoadedKeyframe(currentTime, retryInterval, maxRetries - 1);
          }, retryInterval);
        } 
      },
      cancelUnneededKeyframeImages(currentTime, buffer) {
        const minIndex = Math.max(0, currentTime - buffer);
        const maxIndex = Math.min(this.source.keyframes.length, currentTime + buffer);
  
        Object.keys(this.fetchControllers).forEach(index => {
          const imageObject = this.imageBlobObjects[index];
          if ((index < minIndex || index > maxIndex) && (imageObject?.status === 'loading' || imageObject?.status === 'error')) {
            this.fetchControllers[index].abort();
            delete this.fetchControllers[index];
            this.$set(this.imageBlobObjects, index, { status: 'cancelled', blobUrl: null });
          }
        });
      },
      cancelAllKeyframeFetches() {
        this.isKeyframeLoadingCanceled = true;
        for (const key in this.fetchControllers) {
          if (this.fetchControllers[key]) {
            this.fetchControllers[key].abort();
          }
        }
        // Clear the fetch controllers
        this.fetchControllers = {};
      },
      revokeAllBlobUrls() {
        this.imageBlobObjects.forEach((imageObject, index) => {
          if (imageObject.blobUrl) {
            URL.revokeObjectURL(imageObject.blobUrl);
          }
        });
        this.$emit('setCurrentKeyframe', null)
        this.imageBlobObjects = [];
  
      },
      showLoader(){
        this.player.pause();
        this.isLoading = true;
        this.player.poster = ''
        this.$emit('showTimelineOverlay', true);
      },
      startVolDrag() {
        const volContainer = this.$refs.volumeContainer.getBoundingClientRect();
        const onMouseMove = (e) => {
          if ((e.target.classList.contains('clip-timelines') || e.target.classList.contains('main')) && this.isMediaPlaying) onMouseUp();
          const xVolContainer = e.clientX - volContainer.left;
          let progress = xVolContainer / volContainer.width;
          progress = Math.max(0, Math.min(1, progress));
          if (progress < 0) return
          this.changeVolume(e);
          this.volHandlePos = progress * 100;
          this.$refs.volumeContainer.style.setProperty("--track-progress-position", progress);
        };
        const onMouseDown = (e) => {
          const xVolContainer = e.clientX - volContainer.left;
          let progress = xVolContainer / volContainer.width;
          this.changeVolume(e);
          this.volHandlePos = progress * 100;
          this.$refs.volumeContainer.style.setProperty("--track-progress-position", progress);
        };
        const onMouseUp = (e) => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mousedown', onMouseDown);
          document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mouseup', onMouseUp);
      },
      load(showOverlay) {
        this.cancelAllKeyframeFetches();
        this.revokeAllBlobUrls();
        showOverlay && this.$emit('showTimelineOverlay', true);
        this.isLoading = true;
        this.player.pause()
        this.player.currentTime(0);
        this.totalDuration = this.source.duration;
        if (this.player) {
          this.player.src(this.source);
          this.player.load();
        }
      },
      async handleVideoReady() {
        this.isKeyframeLoadingCanceled = false;
        this.$emit('showTimelineOverlay', false);
        this.isLoading = false;
        if (!this.source.duration) {
          this.source.duration = this.player.duration();
          this.source.mimeType = this.player.currentType();
        }
        this.handleMediaPlay();
        if (this.source.originalAssetUnExtendedData?.mimeType === 'video/mp4'){
          if(this.source.isDeleted){
            await this.fetchMp4Keyframes({url: this.source.src, isLocal: false}); // isLocal should be true, but in this case we have a bug so will use isLocal as false for now...
          } else if (this.source.isClipped){
            await this.fetchMp4Keyframes({url: this.source.clippedAssetObj.filepath, isLocal: true});
          } else if (this.player.currentSrc() === this.source.originalAssetUnExtendedData?.src && !this.source.originalAssetUnExtendedData?.keyframes) {
            await this.fetchMp4Keyframes({url: this.source.originalAssetUnExtendedData.src, isLocal: false});
          } else if (this.source.originalAssetExtendedData && this.player.currentSrc() === this.source.originalAssetExtendedData?.src){
            this.source.isMerged 
            ? await this.fetchMp4Keyframes({url: this.source.originalAssetExtendedData.mergedFilePath, isLocal: true})
            : await this.fetchMp4Keyframes({url: this.source.originalAssetExtendedData.filepath, isLocal: true});
          }
        }
        this.$nextTick(() => {
          if (this.$refs.volumeContainer) {
              this.$refs.volumeContainer.style.setProperty("--track-progress-position", parseFloat((this.volHandlePos / 100).toFixed(2)));
          }
        });
      },
      handleTimeUpdate() {
        this.$emit('timeupdate', this.player.currentTime());
      },
      handleForwardAndRewind(newTime) {
        this.$emit('timeupdate', null, newTime);
      },
      handleMediaEnded() {
        this.player.src(this.source);
      },
      setCurrentTime(time) {
        this.source.currentPlaybackTime = time;
        this.player.currentTime(time);
      },
      handleMediaPlay(){
        this.isMediaPlaying = true;
        this.player.play()
        this.source.isPlaying = true;
      },
      handleMediaPause(){
        this.isMediaPlaying = false;
        this.player.pause()
        this.source.isPlaying = false;
      },
      handleMediaPlayPause(){
        if (this.isLoading) return;
        if (this.isMediaPlaying) this.handleMediaPause()
        else if (!this.isMediaPlaying) this.handleMediaPlay()
      },
      toggleMute() {
        if (this.volume > 0) {
          this.volume = 0;
          this.isMuted = true;
          this.volHandlePos = 0;
          this.$refs.volumeContainer.style.setProperty("--track-progress-position", 0);
        } else {
          this.volume = 1;
          this.isMuted = false;
          this.volHandlePos = this.volume * 100;
          this.$refs.volumeContainer.style.setProperty("--track-progress-position", 1);
        }
        this.player.muted(this.volume === 0);
        this.player.volume(this.volume);
      },
      changeVolume(event) {
        const volumeContainer = this.$refs.volumeContainer
        const clickPosition = event.clientX - volumeContainer.getBoundingClientRect().left;
        const volumeContainerWidth = volumeContainer.offsetWidth;
        let newVolume = clickPosition / volumeContainerWidth;
        newVolume = Math.max(0, Math.min(1, newVolume));
        this.volume = newVolume.toFixed(2);
        if (newVolume > 0 && this.isMuted) {
          this.isMuted = false;
          this.player.muted(false);
        }
        else if (newVolume <= 0 && !this.isMuted) {
          this.isMuted = true;
          this.player.muted(true);
        }
        this.player.volume(newVolume);
      },
      rewindVideo(seconds) {
        if (this.isLoading) return;
        const newTime = Math.max(this.player.currentTime() - seconds, 0);
        this.handleForwardAndRewind(newTime)
        this.player.currentTime(newTime);
      },
      fastForwardVideo(seconds) {
        if (this.isLoading) return;
        const newTime = Math.min(this.player.currentTime() + seconds, this.player.duration());
        this.handleForwardAndRewind(newTime)
        this.player.currentTime(newTime);
      },
      handleKeyPress(event) {
        switch (event.keyCode) {
          case 32: // space
            event.preventDefault();
            this.handleMediaPlayPause();
            break;
          case 37: // Left arrow
            event.preventDefault();
            this.rewindVideo(this.rewindOrFastForwardTime);
            break;
          case 39: // Right arrow
            event.preventDefault();
            this.fastForwardVideo(this.fastForwardTime);
            break;
          case 77: // 'M' key
            event.preventDefault();
            this.toggleMute();
            break;
        }
      },
    },
    mounted() {
      this.player = videojs(this.$refs.videoPlayer, {
        controlBar: false,
        controls: false,
        preload: 'auto',
        autoplay: true,
        muted: this.isMuted,
        volume: this.volume
      });
      this.player.volume(this.volume);
      this.player.muted(this.isMuted);
      this.player.src(this.source)
      this.totalDuration = this.source.duration;
      this.player.on('timeupdate', this.handleTimeUpdate);
      this.player.on('ended', this.handleMediaEnded);
      this.player.on('play', this.handleMediaPlay);
      this.player.on('pause', this.handleMediaPause);
      this.player.on('loadedmetadata', this.handleVideoReady);
      window.addEventListener('keydown', this.handleKeyPress);
    },
    beforeDestroy() {
      this.revokeAllBlobUrls();
      if (this.player) {
        this.player.off('timeupdate', this.handleTimeUpdate);
        this.player.off('ended', this.handleMediaEnded);
        this.player.off('play', this.handleMediaPlay);
        this.player.off('pause', this.handleMediaPause);
        this.player.off('loadedmetadata', this.handleVideoReady);
        this.player.dispose();
        window.removeEventListener('keydown', this.handleKeyPress);
      }
    },
  };
  </script>
  
  <style lang="scss">
    .video-container {
      width: 100%;
      height: 100%;
      position: relative;

      .is-radio{
        color: #999;
        z-index: 10;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        opacity: 0.5;
      }
  
      .is-edited{
        position: absolute;
        top: 1rem;
        left: 1rem;
        z-index: 10;
        color: white;
      }
      .custom-volume-slider {
        position: relative;
        width: 110px;
        z-index: 10;
        background: rgba(153, 153, 153, 0.5);
        margin-left: 1rem;
        padding: 6px 12px;
        border-radius: 12px;
        margin-bottom: 1.5rem;
  
        .volume-bar {
          border-radius: 12px;
          width: 100%;
          height: 4px;
          background-color: #999;
          position: relative;
          cursor: pointer;
  
  
          &::after {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              bottom:0;
              right: calc( 100% - var(--track-progress-position) * 100%);
              background: red;
              height: 100%;
              border-radius: 12px;
            }
          .handle {
            transform: translateX(-5px);
            position: absolute;
            top: -3px;
            height: 10px;
            width: 10px;
            background-color: red;
            border-radius: 50%;
            cursor: pointer;
            z-index: 10;
          }
        }
      }
      &:hover .controls-container, .controls-container.paused{
        opacity: 1;
      }
  
      .controls-container {
        position: absolute;
        padding-bottom: 14px;
        bottom: 0px;
        width: 100%;
        height: 10px;
        opacity: 0;
        transition: opacity 100ms ease-in-out;
  
        i {
          position: relative;
          z-index: 10;
        }
      }
    }
  
    .video-js{
      height: 100%;
      .vjs-loading-spinner{
        z-index: 20;
      }
    }
    .video-player {
      width: 100%;
    }
  
  </style>
  